/* Popup — Store Drawer
 * Nearest-store drawer (LIST + MAP tabs) + inline "Delivery by / Explore N stores"
 * unit injected below the PDP pincode checker.
 *
 * Wiring:
 *  - main-product.liquid dispatches `duroflex:pincode-checked` ({pincode, eta, city})
 *    after a successful serviceability check, and `duroflex:pincode-cleared` when
 *    the shopper taps Change. This file owns everything from there.
 *  - Store data comes from the Duroflex store locator API (hardcoded below by
 *    design — do not move to theme settings).
 *  - Map tab renders via Leaflet/OpenStreetMap (free, default) or Google Maps
 *    (needs API key) depending on the section's map_provider setting.
 */
(function () {
  'use strict';

  var root = document.getElementById('popup-store-drawer');
  if (!root || root.dataset.psdInit === 'true') return;
  root.dataset.psdInit = 'true';

  /* ── API endpoints: code-level only, no theme settings ── */
  var STORES_API = 'https://dfxstorelocatorapi.azurewebsites.net/api/Store/nearby-across';
  var STORES_PAGE_SIZE = 10;
  var STORES_PAYLOAD_BASE = { campaign: '', brand: 'DUROFLEX' };

  var LEAFLET_JS = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
  var LEAFLET_CSS = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
  var GOOGLE_MAPS_JS = 'https://maps.googleapis.com/maps/api/js';

  var cfg = {
    provider: root.dataset.mapProvider || 'leaflet',
    googleKey: root.dataset.googleMapsKey || '',
    titleTemplate: root.dataset.titleTemplate || '[count] Stores Near You',
    storesText: root.dataset.storesText || 'Explore [count] nearest stores',
    viewAllLabel: root.dataset.viewAllLabel || 'View All →',
    legacyEtaPrefix: root.dataset.legacyEtaPrefix || 'Get it By',
    legacySubtext: root.dataset.legacySubtext || '',
    bookLabel: root.dataset.bookLabel || 'Book Free Appointment',
    locationLabel: root.dataset.locationLabel || 'Get Directions',
    loadingText: root.dataset.loadingText || 'Loading stores...',
    emptyText: root.dataset.emptyText || 'No stores found for this pincode.',
    errorText: root.dataset.errorText || 'Failed to load stores.'
  };

  // Festive line under the delivery date (e.g. "Reaches You Before Diwali"), from the section's "Festive line"
  // settings. Shown only when the delivery date (ISO, sent by dfx-delivery.js) is on or before the cut-off date.
  var festive = {
    on: root.dataset.festiveOn === 'true',
    text: root.dataset.festiveText || '',
    before: root.dataset.festiveBefore || '',
    icon: root.dataset.festiveIcon === 'true'
  };
  var DIYA = '<svg viewBox="0 0 24 24" aria-hidden="true">' +
    '<path class="dfx-diwali__flame" d="M12 1.5c2 2.5 2.9 4.2 2.9 5.6a2.9 2.9 0 0 1-5.8 0c0-1.4.9-3.1 2.9-5.6z" fill="#ff9f1c"/>' +
    '<path class="dfx-diwali__flame" d="M12 4.6c.9 1.2 1.3 2 1.3 2.7a1.3 1.3 0 0 1-2.6 0c0-.7.4-1.5 1.3-2.7z" fill="#ffe066"/>' +
    '<path class="dfx-diwali__bowl" d="M1.5 12.5h21c0 4.3-4.7 7.5-10.5 7.5S1.5 16.8 1.5 12.5z"/>' +
    '<path d="M4 12.5h16" stroke="#ffc94d" stroke-width="2" stroke-linecap="round"/></svg>';
  function festiveLine(etaDate) {
    var iso = /^\d{4}-\d{2}-\d{2}$/;
    if (!festive.on || !festive.text || !iso.test(etaDate || '')) return '';
    if (festive.before && !(iso.test(festive.before) && etaDate <= festive.before)) return ''; // empty = every date
    return '<p class="dfx-diwali">' + (festive.icon ? DIYA : '') + '<span>' + esc(festive.text) + '</span>' +
      (festive.icon ? '<i aria-hidden="true">&#10022;</i>' : '') + '</p>';
  }
  window.DfxFestiveLine = festiveLine; // Opera pages draw it under their own date (dfx-delivery.js)

  var els = {
    title: root.querySelector('[data-psd-title]'),
    pinInput: root.querySelector('[data-psd-pin]'),
    checkBtn: root.querySelector('[data-psd-check]'),
    tabList: root.querySelector('[data-psd-tab="list"]'),
    tabMap: root.querySelector('[data-psd-tab="map"]'),
    paneList: root.querySelector('[data-psd-pane="list"]'),
    paneMap: root.querySelector('[data-psd-pane="map"]'),
    mapBox: root.querySelector('[data-psd-map]')
  };

  var state = {
    pincode: '',
    eta: '',
    note: '',
    stores: [],
    count: 0,
    page: 1,
    totalPages: 1,
    activeTab: 'list',
    map: null, // Leaflet or Google map instance
    mapProviderLoaded: false,
    mapStale: true,
    storesPin: '' // pincode whose stores are loaded / loading
  };

  /* ── helpers ── */
  function esc(v) {
    return String(v == null ? '' : v).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function tpl(str, count) {
    return str.replace(/\[count\]/g, count);
  }
  function telDigits(v) {
    return String(v || '').replace(/[^0-9+]/g, '');
  }
  function loadScript(src, onload) {
    var s = document.createElement('script');
    s.src = src;
    s.async = true;
    s.onload = onload;
    s.onerror = function () { showMapNote(cfg.errorText); };
    document.head.appendChild(s);
  }

  /* ── store fetch ── */
  function fetchStores(pin, page) {
    page = page || 1;
    if (page === 1) state.storesPin = String(pin);
    var payload = Object.assign({}, STORES_PAYLOAD_BASE, {
      pincode: String(pin),
      pageNumber: page,
      pageSize: STORES_PAGE_SIZE
    });
    return fetch(STORES_API, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: '*/*' },
      body: JSON.stringify(payload)
    }).then(function (res) {
      if (!res.ok) throw new Error('stores http ' + res.status);
      return res.json();
    }).then(function (data) {
      state.stores = (data.stores || []).slice().sort(function (a, b) {
        return (a.distance_km || 0) - (b.distance_km || 0);
      });
      state.count = data.totalStoresFound || state.stores.length || 0;
      state.page = data.pageNumber || page;
      state.totalPages = data.totalPages || Math.max(1, Math.ceil(state.count / STORES_PAGE_SIZE));
      state.mapStale = true;
      return data;
    }).catch(function (err) {
      if (page === 1) state.storesPin = '';
      throw err;
    });
  }

  /* ── PDP inline unit ── */
  function unitHost() {
    // Sits right below the existing serviceability checker.
    return document.querySelector('.pincode-checker');
  }

  function renderUnit() {
    var host = unitHost();
    if (!host) return;
    // Airboost templates render their own legacy "Get it By / Available at these
    // stores" card (#delivery-status). There, the unit takes over its job but
    // reuses the legacy card's classes so the old airboost styling applies 1:1.
    var legacyCard = document.getElementById('delivery-status');
    var legacyMode = !!legacyCard;
    var unit = document.getElementById('psd-unit');
    if (!unit) {
      unit = document.createElement('div');
      unit.id = 'psd-unit';
      host.insertAdjacentElement('afterend', unit);
    }
    unit.className = legacyMode ? 'psd-root delivery-status psd-unit--legacy' : 'psd-unit psd-root';
    var html = '';
    if (legacyMode) {
      if (state.eta) {
        html += '<h3 class="psd-legacy__title">' + esc(cfg.legacyEtaPrefix) + ' ' + esc(state.eta) + '</h3>' + state.note;
        if (cfg.legacySubtext) {
          html += '<p class="text">' + esc(cfg.legacySubtext) + '</p>';
        }
      }
      if (state.count > 0) {
        html += '<div class="store-details">' +
          '<h3 class="store-text">' + tpl(esc(cfg.storesText), state.count) + '</h3>' +
          '<button type="button" class="store-link psd-legacy__viewall" data-psd-viewall data-gtm-event="SB View All Stores" data-gtm-location="sidebar-pdp">' + esc(cfg.viewAllLabel) + '</button>' +
          '</div>';
      }
    } else {
      if (state.count > 0) {
        html += '<div class="psd-unit__stores">' +
          '<span>📍 ' + tpl(esc(cfg.storesText), '<strong>' + state.count + '</strong>') + '</span>' +
          '<button type="button" class="psd-unit__viewall" data-psd-viewall data-gtm-event="SB View All Stores" data-gtm-location="sidebar-pdp">' + esc(cfg.viewAllLabel) + '</button>' +
          '</div>';
      }
    }
    if (!html) { unit.remove(); return; }
    unit.innerHTML = html;
    var viewAll = unit.querySelector('[data-psd-viewall]');
    if (viewAll) viewAll.addEventListener('click', openDrawer);
    if (legacyMode) legacyCard.classList.add('hide_pincode_block');
  }

  function removeUnit() {
    var unit = document.getElementById('psd-unit');
    if (unit) unit.remove();
  }

  /* ── drawer open/close ── */
  var isDrawerOpen = false;

  function openDrawer() {
    if (isDrawerOpen) return; // Prevent multiple calls
    
    isDrawerOpen = true;
    
    root.classList.add('psd-open');
    root.removeAttribute('hidden');
    
    if (els.pinInput) els.pinInput.value = state.pincode;
    updateTitle();
    switchTab('list');
  }

  function closeDrawer() {
    if (!isDrawerOpen) return; // Prevent multiple calls
    
    root.classList.remove('psd-open');
    isDrawerOpen = false;
  }

  function updateTitle() {
    if (els.title) els.title.textContent = tpl(cfg.titleTemplate, state.count);
  }

  /* ── LIST tab ── */
  function renderList() {
    if (!els.paneList) return;
    if (!state.stores.length) {
      els.paneList.innerHTML = '<div class="psd-empty">' + esc(cfg.emptyText) + '</div>';
      return;
    }
    els.paneList.innerHTML = state.stores.map(function (s) {
      var typeClass = s.storeType === 'FOCO' ? ' psd-store-type--foco'
        : s.storeType === 'COCO' ? '' : ' psd-store-type--ebo';
      var phone = telDigits(s.mobile);
      var wa = telDigits(s.whatsAppNumber).replace(/^\+/, '');
      return '<div class="psd-store-card">' +
        '<div class="psd-store-card__head">' +
          '<div class="psd-store-name">' + esc(s.storeName) + '</div>' +
          (s.storeType ? '<span class="psd-store-type' + typeClass + '">' + esc(s.storeType) + '</span>' : '') +
        '</div>' +
        '<div class="psd-store-addr">' +
          '<svg width="12" height="14" viewBox="0 0 12 14" fill="none" aria-hidden="true"><path d="M6 0C3.79 0 2 1.79 2 4c0 3 4 9 4 9s4-6 4-9c0-2.21-1.79-4-4-4zm0 5.5c-.83 0-1.5-.67-1.5-1.5S5.17 2.5 6 2.5 7.5 3.17 7.5 4 6.83 5.5 6 5.5z" fill="#6B6B6B"/></svg>' +
          // '<span>' + esc(s.address) + (s.city ? ', ' + esc(s.city) : '') + '</span>' +
          // '<span>' + esc(s.address) + (s.storeName && s.storeName.includes(' - ') ? ', ' + esc(s.storeName.split(' - ')[1].trim()) : '') + (s.state ? ', ' + esc(s.state) : '') + '</span>'
          '<span>' + esc(s.address || '') + '</span>' +
        '</div>' +
        '<div class="psd-store-meta">' +
          '<div class="psd-store-rating">⭐ ' + esc(s.averageRating) + '/5 · ' + esc(s.totalReview) + ' reviews</div>' +
          (s.distance_km != null
            ? '<div class="psd-store-dist"><svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true"><polygon points="0,10 5,0 10,10" fill="currentColor"/></svg>' + esc(s.distance_km) + ' kms</div>'
            : '') +
        '</div>' +
        '<div class="psd-store-actions">' +
          (phone
            ? '<a class="psd-phone-link" href="tel:' + esc(phone) + '" data-gtm-event="SB Call Store" data-gtm-store="' + esc(s.storeName) + '" data-gtm-location="sidebar-drawer">' +
              '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.62 3.38 2 2 0 0 1 3.59 1h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.56a16 16 0 0 0 6 6l.92-.92a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>' +
              '0' + esc(s.mobile) + '</a>'
            : '') +
          (s.mapUrl
            ? '<a class="psd-location-link" href="' + esc(s.mapUrl) + '" target="_blank" rel="noopener" data-gtm-event="SB Get Direction" data-gtm-store="' + esc(s.storeName) + '" data-gtm-location="sidebar-drawer">' +
              '<span class="psd-location-link__icon"><svg width="10" height="10" viewBox="0 0 24 24" fill="white" aria-hidden="true"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5A2.5 2.5 0 1 1 12 6.5a2.5 2.5 0 0 1 0 5z"/></svg></span>' +
              esc(cfg.locationLabel) + '</a>'
            : '') +
        '</div>' +
        (wa
          ? '<a class="psd-book-btn" href="https://wa.me/' + esc(wa) + '" target="_blank" rel="noopener" data-gtm-event="SB Book Appointment" data-gtm-store="' + esc(s.storeName) + '" data-gtm-location="sidebar-drawer">' + esc(cfg.bookLabel) + '</a>'
          : '') +
        '</div>';
    }).join('') + renderPagination();
  }

  /* ── pagination controls (ported from the new PDP store-sidebar) ── */
  function renderPagination() {
    var total = state.totalPages;
    var current = state.page;
    if (total <= 1) return '';
    var buttons = '';
    buttons += '<button class="psd-page-btn' + (current === 1 ? ' psd-page-btn--disabled' : '') + '"'
      + (current === 1 ? ' disabled' : ' data-psd-page="' + (current - 1) + '"') + '>← Prev</button>';
    var maxVisible = 5;
    var startPage = Math.max(1, current - Math.floor(maxVisible / 2));
    var endPage = Math.min(total, startPage + maxVisible - 1);
    if (endPage - startPage < maxVisible - 1) startPage = Math.max(1, endPage - maxVisible + 1);
    if (startPage > 1) {
      buttons += '<button class="psd-page-btn" data-psd-page="1">1</button>';
      if (startPage > 2) buttons += '<span class="psd-page-dots">…</span>';
    }
    for (var i = startPage; i <= endPage; i++) {
      buttons += '<button class="psd-page-btn' + (i === current ? ' psd-page-btn--active' : '') + '" data-psd-page="' + i + '">' + i + '</button>';
    }
    if (endPage < total) {
      if (endPage < total - 1) buttons += '<span class="psd-page-dots">…</span>';
      buttons += '<button class="psd-page-btn" data-psd-page="' + total + '">' + total + '</button>';
    }
    buttons += '<button class="psd-page-btn' + (current === total ? ' psd-page-btn--disabled' : '') + '"'
      + (current === total ? ' disabled' : ' data-psd-page="' + (current + 1) + '"') + '>Next →</button>';
    return '<div class="psd-pagination">'
      + '<div class="psd-page-info">Showing page ' + current + ' of ' + total + ' (' + state.count + ' stores)</div>'
      + '<div class="psd-page-buttons">' + buttons + '</div>'
      + '</div>';
  }

  function goToPage(page) {
    if (page < 1 || page > state.totalPages || page === state.page) return;
    if (els.paneList) els.paneList.innerHTML = '<div class="psd-loading">' + esc(cfg.loadingText) + '</div>';
    fetchStores(state.pincode, page).then(function () {
      renderList();
      var body = root.querySelector('.psd-body');
      if (body) body.scrollTop = 0;
    }).catch(function () {
      if (els.paneList) els.paneList.innerHTML = '<div class="psd-error">' + esc(cfg.errorText) + '</div>';
    });
  }

  /* ── MAP tab ── */
  function showMapNote(msg) {
    if (els.paneMap) {
      els.paneMap.innerHTML = '<div class="psd-map-note">' + esc(msg) + '</div>' + mapBoxHtml();
      els.mapBox = root.querySelector('[data-psd-map]');
    }
  }
  function mapBoxHtml() {
    return '<div class="psd-map" data-psd-map></div>';
  }
  function resetMapBox() {
    if (!els.paneMap) return;
    els.paneMap.innerHTML = mapBoxHtml();
    els.mapBox = root.querySelector('[data-psd-map]');
    state.map = null;
  }

  function mappableStores() {
    return state.stores.filter(function (s) {
      return s.lat != null && s.long != null && !isNaN(parseFloat(s.lat)) && !isNaN(parseFloat(s.long));
    });
  }

  function popupHtml(s) {
    return '<div class="psd-map-popup"><strong>' + esc(s.storeName) + '</strong>' +
      (s.distance_km != null ? esc(s.distance_km) + ' kms away<br>' : '') +
      (s.mapUrl ? '<a href="' + esc(s.mapUrl) + '" target="_blank" rel="noopener">' + esc(cfg.locationLabel) + '</a>' : '') +
      '</div>';
  }

  function renderMap() {
    var stores = mappableStores();
    if (!stores.length) {
      showMapNote(cfg.emptyText);
      return;
    }
    if (cfg.provider === 'google') {
      if (!cfg.googleKey) {
        showMapNote('Google Maps API key missing — add it in the Store Drawer section settings, or switch the map provider to Leaflet.');
        return;
      }
      ensureGoogle(function () { drawGoogleMap(stores); });
    } else {
      ensureLeaflet(function () { drawLeafletMap(stores); });
    }
  }

  function ensureLeaflet(cb) {
    if (window.L && window.L.map) { cb(); return; }
    if (!document.querySelector('link[href="' + LEAFLET_CSS + '"]')) {
      var link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = LEAFLET_CSS;
      document.head.appendChild(link);
    }
    loadScript(LEAFLET_JS, cb);
  }

  function ensureGoogle(cb) {
    if (window.google && window.google.maps) { cb(); return; }
    if (window.__psdGmapsLoading) {
      window.__psdGmapsQueue.push(cb);
      return;
    }
    window.__psdGmapsLoading = true;
    window.__psdGmapsQueue = [cb];
    window.__psdGmapsReady = function () {
      (window.__psdGmapsQueue || []).forEach(function (fn) { fn(); });
      window.__psdGmapsQueue = [];
    };
    loadScript(GOOGLE_MAPS_JS + '?key=' + encodeURIComponent(cfg.googleKey) + '&callback=__psdGmapsReady&loading=async');
  }

  function drawLeafletMap(stores) {
    resetMapBox();
    var map = window.L.map(els.mapBox, { scrollWheelZoom: false });
    window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
    }).addTo(map);
    var bounds = [];
    stores.forEach(function (s) {
      var ll = [parseFloat(s.lat), parseFloat(s.long)];
      bounds.push(ll);
      window.L.marker(ll).addTo(map).bindPopup(popupHtml(s));
    });
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 15 });
    state.map = map;
    state.mapStale = false;
    // Drawer animates in; sizes settle a tick later.
    setTimeout(function () { map.invalidateSize(); }, 350);
  }

  function drawGoogleMap(stores) {
    resetMapBox();
    var map = new window.google.maps.Map(els.mapBox, {
      zoom: 12,
      mapTypeControl: false,
      streetViewControl: false
    });
    var bounds = new window.google.maps.LatLngBounds();
    var info = new window.google.maps.InfoWindow();
    stores.forEach(function (s) {
      var pos = { lat: parseFloat(s.lat), lng: parseFloat(s.long) };
      bounds.extend(pos);
      var marker = new window.google.maps.Marker({ position: pos, map: map, title: s.storeName });
      marker.addListener('click', function () {
        info.setContent(popupHtml(s));
        info.open(map, marker);
      });
    });
    map.fitBounds(bounds, 40);
    state.map = map;
    state.mapStale = false;
  }

  /* ── tabs ── */
  function switchTab(tab) {
    state.activeTab = tab;
    if (els.tabList) els.tabList.classList.toggle('psd-tab--active', tab === 'list');
    if (els.tabMap) els.tabMap.classList.toggle('psd-tab--active', tab === 'map');
    if (els.paneList) els.paneList.classList.toggle('psd-pane--active', tab === 'list');
    if (els.paneMap) els.paneMap.classList.toggle('psd-pane--active', tab === 'map');
    if (tab === 'list') {
      renderList();
    } else if (state.mapStale || !state.map) {
      renderMap();
    } else if (state.map && state.map.invalidateSize) {
      setTimeout(function () { state.map.invalidateSize(); }, 50);
    }
  }

  /* ── drawer pincode re-check (stores only; PDP serviceability untouched) ── */
  function recheckFromDrawer() {
    var pin = (els.pinInput && els.pinInput.value || '').trim();
    if (!/^[0-9]{6}$/.test(pin)) return;
    state.pincode = pin;
    if (window.DfxDelivery) window.DfxDelivery.check(pin);
    if (els.paneList) els.paneList.innerHTML = '<div class="psd-loading">' + esc(cfg.loadingText) + '</div>';
    fetchStores(pin).then(function () {
      updateTitle();
      renderUnit();
      switchTab(state.activeTab);
    }).catch(function () {
      if (els.paneList) els.paneList.innerHTML = '<div class="psd-error">' + esc(cfg.errorText) + '</div>';
    });
  }

  /* ── wire up static drawer controls ── */
  root.querySelectorAll('[data-psd-close]').forEach(function (el) {
    el.addEventListener('click', closeDrawer);
  });
  if (els.tabList) els.tabList.addEventListener('click', function () { switchTab('list'); });
  if (els.paneList) els.paneList.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest ? e.target.closest('[data-psd-page]') : null;
    if (!btn) return;
    var page = parseInt(btn.getAttribute('data-psd-page'), 10);
    if (page) goToPage(page);
  });
  if (els.tabMap) els.tabMap.addEventListener('click', function () { switchTab('map'); });
  if (els.checkBtn) {
    els.checkBtn.setAttribute('data-gtm-event', 'SB Check Pincode');
    els.checkBtn.setAttribute('data-gtm-location', 'sidebar-drawer');
    els.checkBtn.addEventListener('click', recheckFromDrawer);
  }
  if (els.pinInput) {
    els.pinInput.addEventListener('input', function () {
      this.value = this.value.replace(/[^0-9]/g, '').slice(0, 6);
    });
    els.pinInput.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') recheckFromDrawer();
    });
  }
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && root.classList.contains('psd-open')) closeDrawer();
  });

  /* ── PDP integration events ── */
  window.addEventListener('duroflex:pincode-checked', function (e) {
    var d = (e && e.detail) || {};
    if (!d.pincode) return;
    var sameStores = state.storesPin === String(d.pincode);
    state.pincode = String(d.pincode);
    state.eta = d.eta || '';
    state.note = festiveLine(d.etaDate);
    // a size / quantity change re-checked the same pincode: only the date moved, the stores did not
    if (sameStores) { renderUnit(); updateTitle(); return; }
    // Render synchronously so the legacy #delivery-status card is hidden
    // immediately, instead of flashing until the async store fetch resolves.
    renderUnit();
    fetchStores(state.pincode).then(function () {
      renderUnit();
      updateTitle();
      if (root.classList.contains('psd-open')) switchTab(state.activeTab);
    }).catch(function () {
      // Stores are an enhancement — still show the ETA line if we have one.
      state.stores = [];
      state.count = 0;
      renderUnit();
    });
  });

  window.addEventListener('duroflex:pincode-cleared', function (e) {
    var d = (e && e.detail) || {};
    state.eta = '';
    state.note = '';
    // not serviceable while the shopper browses stores in the drawer: keep the drawer and its stores
    if (d.reason === 'unserviceable' && root.classList.contains('psd-open')) { removeUnit(); return; }
    state.stores = [];
    state.count = 0;
    state.storesPin = '';
    removeUnit();
    closeDrawer();
  });
})();
